import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './index.css';
import HomePage from './components/HomePage';
import ContentCheckerPage from './ContentCheckerPage'; // Renamed App component
import LoginPage from './components/LoginPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import ProfilePage from './components/ProfilePage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import DashboardLayout from './components/DashboardLayout';
import DashboardPage from './components/DashboardPage';
import HistoryPage from './components/HistoryPage';
import MyVideosPage1 from './components/MyVideosPage1';
import ArticlesPage from './components/ArticlesPage';
import UploadVideoPage from './components/UploadVideoPage';
import NotificationsPage from './components/NotificationsPage';
import PricingPage from './components/PricingPage';
import SharedReportPage from './components/SharedReportPage';
// SEO Pages
import YouTubeDemonetizationGuide from './components/seo/YouTubeDemonetizationGuide';
import YouTubeGuidelinesGuide from './components/seo/YouTubeGuidelinesGuide';
import TikTokGuidelinesGuide from './components/seo/TikTokGuidelinesGuide';
import CopyrightMusicGuide from './components/seo/CopyrightMusicGuide';
import AppealStrikesGuide from './components/seo/AppealStrikesGuide';
import VideoComplianceChecker from './components/seo/VideoComplianceChecker';
import AIContentGuidelinesGuide from './components/seo/AIContentGuidelinesGuide';
import SponsoredContentFTCGuide from './components/seo/SponsoredContentFTCGuide';
import ShadowbanRecoveryGuide from './components/seo/ShadowbanRecoveryGuide';
import YouTubeAdRevenueGuide from './components/seo/YouTubeAdRevenueGuide';
import TikTokFYFGuide from './components/seo/TikTokFYFGuide';
import YouTubeProfanityGuide from './components/seo/YouTubeProfanityGuide';
import Clarity from '@microsoft/clarity';
import { store } from './app/store';
import { Provider, useSelector } from 'react-redux';
import { selectIsAuthenticated } from './features/auth/authSlice';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Initialize Microsoft Clarity (production only)
const CLARITY_PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID;
if (CLARITY_PROJECT_ID && window.location.hostname === 'mycleanstudio.com') {
  Clarity.init(CLARITY_PROJECT_ID);
}

function RequireAuth({ children }) {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  if (isAuthenticated) {
    return children;
  }

  return (
    <div className="access-denied-page">
      <h1 className="access-denied-title">You need to login to access this page</h1>
      <p className="access-denied-text">
        Please use the following buttons to login or create an account.
      </p>
      <div className="access-denied-actions">
        <Link to="/login" className="access-denied-btn primary">Login</Link>
        <Link to="/signup" className="access-denied-btn secondary">Create Free Account</Link>
      </div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Provider store={store}>
        <BrowserRouter>
          <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<LoginPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/articles" element={<ArticlesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/r/:shareId" element={<SharedReportPage />} />
              {/* Dashboard Routes */}
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route
                  index
                  element={
                    <RequireAuth>
                      <DashboardPage />
                    </RequireAuth>
                  }
                />
                <Route path="new-check" element={<ContentCheckerPage />} />
                <Route path="history" element={<HistoryPage />} />
                <Route
                  path="videos1"
                  element={
                    <RequireAuth>
                      <MyVideosPage1 />
                    </RequireAuth>
                  }
                />
                <Route
                  path="upload"
                  element={
                    <RequireAuth>
                      <UploadVideoPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="profile"
                  element={
                    <RequireAuth>
                      <ProfilePage />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="/notifications" element={<DashboardLayout />}>
                <Route
                  index
                  element={
                    <RequireAuth>
                      <NotificationsPage />
                    </RequireAuth>
                  }
                />
              </Route>
              {/* SEO Pages - Guides */}
              <Route path="/guides/youtube-demonetization" element={<YouTubeDemonetizationGuide />} />
              <Route path="/guides/youtube-guidelines" element={<YouTubeGuidelinesGuide />} />
              <Route path="/guides/tiktok-guidelines" element={<TikTokGuidelinesGuide />} />
              <Route path="/guides/copyright-music-detection" element={<CopyrightMusicGuide />} />
              <Route path="/guides/appeal-strikes" element={<AppealStrikesGuide />} />
              <Route path="/guides/ai-content-guidelines" element={<AIContentGuidelinesGuide />} />
              <Route path="/guides/sponsored-content-ftc" element={<SponsoredContentFTCGuide />} />
              <Route path="/guides/shadowban-recovery" element={<ShadowbanRecoveryGuide />} />
              <Route path="/guides/youtube-ad-revenue-compliance" element={<YouTubeAdRevenueGuide />} />
              <Route path="/guides/tiktok-fyf-eligibility" element={<TikTokFYFGuide />} />
              <Route path="/guides/youtube-profanity-rules" element={<YouTubeProfanityGuide />} />
              {/* SEO Pages - Features */}
              <Route path="/features/video-compliance-checker" element={<VideoComplianceChecker />} />
            </Routes>
        </BrowserRouter>
      </Provider>
    </GoogleOAuthProvider>
  </React.StrictMode>
);
